import { useEffect, useState } from 'react';
import { celebrate } from '@hotjar/webapp/utils/confetti';
import {
  bus,
  trackingCodeInstalled,
  trackingCodeVerificationFinished,
} from '@hotjar/webapp/utils/events';
import { useCurrentSite } from '@hotjar/site';
import { useCurrentUser } from '@hotjar/user';
import { usePermission } from '@hotjar/webapp/domains/authorization';
import { enablePostInstallCard } from '../../SpaceOverview/components';
import type { CMS } from '../steps';
import { STEPS } from '../steps';
import { useSetupWizardNavigation } from './useSetupWizardNavigation';
import { useSiteMetadata } from './useSiteMetadata';

interface GTMState {
  installed: boolean;
  detected: boolean;
}

export interface SetupWizardData {
  setCMS: (cms: CMS) => void;
  goToStep: (step: string) => void;
  goToInsights: () => void;
  isLoading: boolean;
  step: string | undefined;
  cms?: CMS;
  gtm: GTMState;
}

export const useSetupWizard = (): SetupWizardData => {
  const { data: currentSite } = useCurrentSite();
  const { isLoading: isCurrentUserLoading } = useCurrentUser();
  const siteId = currentSite?.id;
  const siteUrl = currentSite?.url;
  const { step, isLoading, goToStep, goToInsights } = useSetupWizardNavigation();
  const siteMetadata = useSiteMetadata(step === STEPS.INSTALLATION_METHOD);
  const [isSiteAnalyzed, setIsSiteAnalyzed] = useState(false);

  const [cms, setCMS] = useState<CMS>();
  const [withGTM, setWithGTM] = useState(false);
  const [isGtmInstalled, setIsGtmInstalled] = useState(false);

  const hasSiteUpdatePermission = usePermission('sites-update');

  useEffect(() => {
    const unsubscribe = bus.subscribe(trackingCodeInstalled, () => {
      if (siteId) {
        enablePostInstallCard(siteId);
        goToInsights();
      }
    });

    return () => {
      unsubscribe();
    };
  }, [goToInsights, siteId]);

  useEffect(() => {
    const unsubscribe = bus.subscribe(trackingCodeVerificationFinished, () => {
      if (siteId) {
        enablePostInstallCard(siteId);
        celebrate();
        goToInsights();
      }
    });

    return () => {
      unsubscribe();
    };
  });

  useEffect(() => {
    if (siteMetadata) {
      const { gtm, cms: detectedCms, gtmInstalled, finished } = siteMetadata;
      setIsSiteAnalyzed(!!finished);
      if (hasSiteUpdatePermission && gtm && gtm !== 'false') {
        setWithGTM(true);
        setIsGtmInstalled(!!gtmInstalled);
      } else if (detectedCms !== 'unknown') {
        setCMS(detectedCms as CMS);
      }
    }
  }, [siteMetadata, hasSiteUpdatePermission, step]);

  return {
    cms,
    gtm: {
      detected: withGTM,
      installed: isGtmInstalled,
    },
    step,
    isLoading:
      isLoading ||
      (step === STEPS.INSTALLATION_METHOD && !isSiteAnalyzed && !!siteUrl) ||
      isCurrentUserLoading,
    setCMS,
    goToStep,
    goToInsights,
  };
};
